import { useState } from 'react';
import '@aws-amplify/ui-react/styles.css';
import LivenessQuickStartReact from './components/AWSLivenessComponent';
import CoachingInstructions from './components/CoachingInstructions';
import { useWakeLock } from './hooks/useWakeLock';
import AWSLivenessV2 from './AWSLivenessV2';
import { useTranslation } from './hooks/useTranslation';
import { reportEvent } from '@/shared/helpers/report-event.helper';
import { LogType } from '@/shared/types/logType';

const CameraSlashIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="m3 3 18 18M9.5 6H7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3.5L16.5 9H14l-2-3H9.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 11.5a2.5 2.5 0 0 0 2.5 2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export default function AWSLiveness({ livenessSession, vendorData, personUuid, vendor }: { livenessSession: string, vendorData: any, personUuid?: string, vendor?: string }) {
    // V2 vs V1 is chosen by the backend via vendor_data.use_v2_ui (set by the
    // aws_liveness_v2 monolith flag in persons-api SessionOrigination).
    if (vendorData?.use_v2_ui === true) {
        return <AWSLivenessV2 livenessSession={livenessSession} vendorData={vendorData} personUuid={personUuid} vendor={vendor} />;
    }

    return <AWSLivenessV1 livenessSession={livenessSession} vendorData={vendorData} personUuid={personUuid} vendor={vendor} />;
}

function AWSLivenessV1({ livenessSession, vendorData, personUuid, vendor }: { livenessSession: string, vendorData: any, personUuid?: string, vendor?: string }) {
    useWakeLock();
    const { t } = useTranslation();
    const [hasCameraAccessError, setHasCameraAccessError] = useState(false);

    // Defensive checks for vendorData properties
    if (!vendorData?.authentication || !vendorData?.session_id) {
        return (
            <div style={{
                width: '100%',
                minHeight: '100dvh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f0f0f0'
            }}>
                <div style={{ textAlign: 'center', color: '#666' }}>
                    <h2>Configuration Error</h2>
                    <p>Missing required AWS liveness configuration data.</p>
                </div>
            </div>
        );
    }

    const { authentication, session_id, show_coaching, region } = vendorData;

    if (hasCameraAccessError) {
        return (
            <div data-testid="aws-liveness-v1-camera-access-error" className="liveness-v2-fullscreen">
                <div className="liveness-v2-modal liveness-v2-modal--instructions">
                    <div className="liveness-v2-modal__icon liveness-v2-modal__icon--camera"><CameraSlashIcon /></div>
                    <h2 className="liveness-v2-modal__title">{t('LIVENESS_V2_CAMERA_BLOCKED_TITLE')}</h2>
                    <p className="liveness-v2-modal__body">{t('LIVENESS_V2_CAMERA_BLOCKED_BODY')}</p>
                    <ol className="liveness-v2-permission-steps">
                        <li>{t('LIVENESS_V2_CAMERA_BLOCKED_STEP_ONE')}</li>
                        <li>{t('LIVENESS_V2_CAMERA_BLOCKED_STEP_TWO')}</li>
                        <li>{t('LIVENESS_V2_CAMERA_BLOCKED_STEP_THREE')}</li>
                        <li>{t('LIVENESS_V2_CAMERA_BLOCKED_STEP_FOUR')}</li>
                    </ol>
                    <button type="button" className="liveness-v2-modal__button" onClick={() => setHasCameraAccessError(false)}>
                        {t('LIVENESS_V2_CAMERA_BLOCKED_BUTTON')}
                    </button>
                </div>
            </div>
        );
    }

    // Cap width so the SDK's aspect-ratio-driven video height, plus its own
    // 3-line photosensitivity banner, video padding and Start button (~260px
    // total), fits within the viewport. `overflow: auto` on the parent absorbs
    // any residual overshoot (locale wraps to 4 lines, etc.).
    return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div data-testid="aws-liveness-v1-sdk-wrapper" data-dd-privacy="hidden" style={{ width: '100%', maxWidth: 'min(100%, calc((100dvh - 260px) * 3 / 4))' }}>
                <LivenessQuickStartReact
                    awsSessionId={session_id}
                    livenessSessionId={livenessSession}
                    region={region}
                    onCameraAccessError={(cause) => {
                        reportEvent('LIVENESS_AWS_CAMERA_ACCESS_ERROR', {
                            event: 'LIVENESS_AWS_CAMERA_ACCESS_ERROR',
                            vendor,
                            engineVersion: 'aws-v1',
                            cameraErrorName: cause?.errorName ?? null,
                            cameraErrorMessage: cause?.errorMessage ?? null,
                            livenessSessionId: livenessSession,
                            personUuid,
                            timestamp: new Date().toISOString(),
                        }, LogType.ERROR);
                        setHasCameraAccessError(true);
                    }}
                    credentials={{
                        accessKeyId: authentication.access_key_id,
                        secretAccessKey: authentication.secret_access_key,
                        sessionToken: authentication.session_token
                    }} />
            </div>
        </div>
    );
}